import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Academy,
  AcademySport,
  AgeGroup,
  Coach,
  Lesson,
  ProRataPreviewResponse,
  PublicProgramSummary,
  Term,
} from '@court-sync/shared';
import { getActiveAcademySports } from '@/lib/sportPresentation';
import { academyService } from '../services/academy';
import { lessonService } from '../services/lesson';
import { termService } from '../services/term';
import { coachService } from '../services/coach';
import { getPublicPrograms } from '../services/program';
import { STALE_TIME } from '../queryClient';
import { queryKeys } from './keys';
import { usePublicLocations } from './locations';
import { useAgeGroups } from './ageGroups';

function formatQueryError(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

export function buildProRataPreviewByLessonId(
  lessons: Lesson[]
): Record<string, ProRataPreviewResponse | undefined> {
  const map: Record<string, ProRataPreviewResponse | undefined> = {};
  for (const lesson of lessons) {
    if (lesson.proRataPreview) {
      map[lesson.id] = lesson.proRataPreview;
    }
  }
  return map;
}

export interface UsePublicAcademyOptions {
  slug?: string;
  subdomainLabel?: string;
  /** Preloaded academy (e.g. subdomain host context) — seeds React Query cache to avoid duplicate fetch. */
  seed?: Academy | null;
  /** When false, waits for subdomain host resolution before fetching. */
  enabled?: boolean;
}

export function usePublicAcademy(options: UsePublicAcademyOptions) {
  const { slug, subdomainLabel, seed, enabled = true } = options;
  const queryClient = useQueryClient();

  const queryKey = subdomainLabel
    ? queryKeys.academies.publicBySubdomain(subdomainLabel)
    : slug
      ? queryKeys.academies.publicBySlug(slug)
      : null;

  const { data: academy = null, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKey ?? ['academies', 'public', 'disabled'],
    queryFn: async () => {
      let result: Academy;
      if (subdomainLabel) {
        result = await academyService.getAcademyBySubdomain(subdomainLabel);
      } else if (slug) {
        result = await academyService.getAcademyBySlug(slug);
      } else {
        throw new Error('slug or subdomainLabel is required');
      }
      queryClient.setQueryData<Academy>(queryKeys.academies.detail(result.id), result);
      return result;
    },
    enabled: enabled && !!queryKey,
    staleTime: STALE_TIME.MEDIUM,
    initialData: seed ?? undefined,
    initialDataUpdatedAt: seed ? new Date(seed.updatedAt).getTime() : undefined,
  });

  const resolvedAcademy = academy ?? seed ?? null;

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    academy: resolvedAcademy,
    isLoading: enabled && !!queryKey && !resolvedAcademy && (isLoading || isFetching),
    error: formatQueryError(error),
    refresh,
  };
}

export function usePublicLessons(academyId?: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && !!academyId;
  const queryKey = queryKeys.lessons.publicList(academyId ?? '');

  const { data: lessons = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => lessonService.getPublicLessons(academyId!),
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    lessons,
    isLoading,
    error: formatQueryError(error),
    refresh,
  };
}

export function usePublicTerms(academyId?: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && !!academyId;
  const queryKey = queryKeys.terms.publicList(academyId ?? '');

  const { data: terms = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => termService.getPublicTerms(academyId!),
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    terms,
    isLoading,
    error: formatQueryError(error),
    refresh,
  };
}

export function usePublicCoaches(academyId?: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && !!academyId;
  const queryKey = queryKeys.coaches.publicList(academyId ?? '');

  const { data: coaches = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => coachService.getPublicCoaches(academyId!),
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    coaches,
    isLoading,
    error: formatQueryError(error),
    refresh,
  };
}

export function usePublicPrograms(academyId?: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && !!academyId;
  const queryKey = queryKeys.programs.publicList(academyId ?? '');

  const { data: publicPrograms = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getPublicPrograms(academyId!);
      return response.success && response.data ? response.data : ([] as PublicProgramSummary[]);
    },
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    publicPrograms,
    isLoading,
    error: formatQueryError(error),
    refresh,
  };
}

export interface PublicCatalogData {
  academy: Academy | null;
  lessons: Lesson[];
  terms: Term[];
  academySports: AcademySport[];
  coaches: Coach[];
  ageGroups: AgeGroup[];
  publicLocations: ReturnType<typeof usePublicLocations>['locations'];
  publicPrograms: PublicProgramSummary[];
  proRataPreviewByLessonId: Record<string, ProRataPreviewResponse | undefined>;
  isLoading: boolean;
  error: string | null;
}

export interface UsePublicCatalogOptions {
  slug?: string;
  subdomainLabel?: string;
  seedAcademy?: Academy | null;
  hostLoading?: boolean;
  includeCoaches?: boolean;
  enabled?: boolean;
}

/** Bundled public academy catalog with React Query caching across browse pages. */
export function usePublicCatalog(options: UsePublicCatalogOptions): PublicCatalogData {
  const {
    slug,
    subdomainLabel,
    seedAcademy,
    hostLoading = false,
    includeCoaches = false,
    enabled = true,
  } = options;

  const academyEnabled =
    enabled && (subdomainLabel ? !hostLoading || !!seedAcademy : !!slug);

  const { academy, isLoading: academyLoading, error: academyError } = usePublicAcademy({
    slug: subdomainLabel ? undefined : slug,
    subdomainLabel,
    seed: seedAcademy,
    enabled: academyEnabled,
  });

  const resolvedAcademy = academy ?? seedAcademy ?? null;

  const academyId = resolvedAcademy?.id;
  const catalogEnabled = enabled && !!academyId;

  const { lessons, isLoading: lessonsLoading, error: lessonsError } = usePublicLessons(academyId, {
    enabled: catalogEnabled,
  });
  const { terms, isLoading: termsLoading, error: termsError } = usePublicTerms(academyId, {
    enabled: catalogEnabled,
  });
  const { coaches, isLoading: coachesLoading, error: coachesError } = usePublicCoaches(academyId, {
    enabled: catalogEnabled && includeCoaches,
  });
  const { ageGroups: rawAgeGroups, isLoading: ageGroupsLoading, error: ageGroupsError } =
    useAgeGroups(academyId, { enabled: catalogEnabled });
  const { locations: publicLocations, isLoading: locationsLoading, error: locationsError } =
    usePublicLocations(academyId, { enabled: catalogEnabled });
  const { publicPrograms, isLoading: programsLoading, error: programsError } = usePublicPrograms(
    academyId,
    { enabled: catalogEnabled }
  );

  const ageGroups = useMemo(
    () => rawAgeGroups.filter((g) => g.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [rawAgeGroups]
  );

  const academySports = useMemo(
    () => getActiveAcademySports(resolvedAcademy?.academySports),
    [resolvedAcademy?.academySports]
  );

  const proRataPreviewByLessonId = useMemo(
    () => buildProRataPreviewByLessonId(lessons),
    [lessons]
  );

  const isLoading =
    academyLoading ||
    lessonsLoading ||
    termsLoading ||
    (includeCoaches && coachesLoading) ||
    ageGroupsLoading ||
    locationsLoading ||
    programsLoading;

  const error =
    academyError ??
    lessonsError ??
    termsError ??
    (includeCoaches ? coachesError : null) ??
    ageGroupsError ??
    locationsError ??
    programsError;

  return {
    academy: resolvedAcademy,
    lessons,
    terms,
    academySports,
    coaches,
    ageGroups,
    publicLocations,
    publicPrograms,
    proRataPreviewByLessonId,
    isLoading,
    error,
  };
}
